import * as CL from '@design-system/component-library';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { dsClass } from '../../common/constants/dsClasses.js';
import { paths } from '../../common/constants/pathVariables.js';
import { useConductrics } from '../../common/hooks/useConductrics.js';
import { t } from '../../common/i18n/index.js';

import './KukaSoittaaOrder.scss';

interface BannerProps {
  className?: string;
  title: string;
  description: string;
  learnMoreText: string;
  orderButtonText: string;
}

const GOAL_CODE = 'kukasoittaa_banner_click';
const AGENT_CODE = 'noe_kukasoittaa_banner_agent';

const Banner = ({ className, title, description, learnMoreText, orderButtonText }: BannerProps) => {
  const { sendGoalEvent, isLoaded: conductricsLoaded } = useConductrics();

  const handleClick = () => {
    if (conductricsLoaded) {
      sendGoalEvent(GOAL_CODE);
    }
  };

  return (
    <CL.Disclaimer
      analytics-parent-element="NOE Kuka Soittaa banner"
      className={`kukasoittaa-banner ${className || ''}`}
      disclaimerType="info"
      icon={<CL.Icon icon="star" type="regular" aria-hidden="true" />}
      title={title}
      visible
    >
      <p className="ds-white-space--pre-wrap">{description}</p>
      <div className={dsClass.MARGIN_TOP_3}>
        <Link to={paths.KUKASOITTAA} className={dsClass.LINK}>
          {learnMoreText}
        </Link>
      </div>
      <Link
        onClick={handleClick}
        to={paths.PS_MOBILE_SUBSCRIPTION_MULTISUB_ADDONS}
        className={`${dsClass.MARGIN_TOP_3} ${dsClass.PADDING_HORIZONTAL_3} ${dsClass.BUTTON} ${dsClass.PADDING_VERTICAL_2} ${dsClass.BUTTON_COLOR_LIGHT}`}
      >
        {orderButtonText}
      </Link>
    </CL.Disclaimer>
  );
};

interface KukaSoittaaBannerProps {
  className?: string;
}

export const KukaSoittaaBanner = ({ className }: KukaSoittaaBannerProps) => {
  const { getVariation, isLoaded: conductricsLoaded } = useConductrics();
  // A: Original banner texts
  // B: New banner texts
  const [variation, setVariation] = useState<'A' | 'B'>('A');

  useEffect(() => {
    const fetchVariation = async () => {
      const result = await getVariation<'A' | 'B'>(AGENT_CODE);
      setVariation(result);
    };

    if (conductricsLoaded) {
      fetchVariation();
    }
  }, [conductricsLoaded, getVariation]);

  return (
    <>
      {variation === 'A' && (
        <Banner
          className={className}
          title={t.AQON(
            'Elisa Kuka Soittaa is now available at an introductory price of 2,99 € / month / subscription, VAT 0%'
          )}
          description={t.AQOO(
            `With the Elisa Kuka Soittaa service, you can see who is calling you. The service automatically displays the caller's name on your phone without the need to download separate apps. Elisa Kuka Soittaa is now available at an introductory price of 2,99 € / month / subscription, VAT 0% (normally 3,99 € / month / subscription, VAT 0%).`
          )}
          learnMoreText={t.AQOP('Read more about the Kuka Soittaa service')}
          orderButtonText={t.AQOQ('Order the Kuka Soittaa service')}
        />
      )}
      {variation === 'B' && (
        <Banner
          className={className}
          title={t.ETR5('Elisa Kuka Soittaa now at a promotional price of 2,99 € / month VAT 0%')}
          description={t.O9N8(
            `With the Elisa Kuka Soittaa service, you can see who is calling you. The service automatically displays the caller's name on your phone without the need to download separate apps. Elisa Kuka Soittaa is now available for a limited time at an introductory price of 2,99 € / month / subscription, VAT 0% (normally 3,99 € / month / subscription, VAT 0%).\n\nYou can order the Elisa Kuka Soittaa service effortlessly for one or more business subscriptions directly in Business OmaElisa!`
          )}
          learnMoreText={t.AQOP('Read more about the Kuka Soittaa service')}
          orderButtonText={t.Z1U2('Order Elisa Kuka Soittaa')}
        />
      )}
    </>
  );
};
